'use server';

import { headers } from 'next/headers';
import axios from 'axios';
import { UAParser } from 'ua-parser-js';
import { createSession } from '@/features/auth/session';
import { BASE_URL, DEVICE_ID, getAuthHeaders } from '@/features/auth/constant';
import { mapUserPayload, handleAxiosError } from '@/libs/utils';
import {
  parseActivationLoginHintFromUnknown,
  type PostActivationLoginTarget,
} from '@/features/auth/utils';
import { mapApiActivity } from '@/libs/utils';
import type {
  ActivityFilters,
  UserOption,
} from '@/features/system-management/components/hook/types';
import type {
  MeApiResponse,
  LogoutApiResponse,
  BackendUsersMeResponse,
  LoginDevice,
} from './types';
import { ApiNotificationSettings } from '@/features/system-management/components/hook/types';
import { mapApiNotifToFront } from '@/libs/utils';
import type { NotificationSettings } from '@/features/system-management/components/hook/types';
import { getAccessToken } from '@/libs/utils';

/**
 * Expose le token d’accès au client via server action (usage client avancé).
 * Attention: ne pas utiliser côté UI “standard” sauf besoin explicite.
 */
export async function getAccessTokenAction(): Promise<string | null> {
  return getAccessToken();
}

async function getDeviceInfo(): Promise<LoginDevice> {
  const headerStore = await headers();
  const result = UAParser(headerStore.get('user-agent') ?? '');
  return {
    device_type: result.device.type ?? 'web',
    device_name: result.device.model ?? 'Browser',
    os_name: result.os.name ?? 'Unknown',
    os_version: result.os.version ?? '0',
    browser_name: result.browser.name ?? 'Unknown',
    browser_version: result.browser.major ?? '0',
  };
}

export interface LoginActionResult {
  success: boolean;
  message?: string;
  error?: string;
}

export async function loginAction(
  email: string,
  password: string
): Promise<LoginActionResult> {
  if (!BASE_URL)
    return { success: false, error: 'Configuration serveur manquante.' };

  try {
    const { data } = await axios.post<{
      access_token: string;
      refresh_token: string;
    }>(
      `${BASE_URL}/auth/login`,
      { email, password, device: await getDeviceInfo() },
      {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          'X-Client-Device-Id': DEVICE_ID,
        },
      }
    );

    if (!data.access_token || !data.refresh_token) {
      return { success: false, error: 'Réponse inattendue du serveur.' };
    }

    await createSession({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    });
    return { success: true, message: 'Connexion réussie' };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const detail = handleAxiosError(
        err,
        `Erreur serveur (${status ?? 'inconnue'}).`
      );

      if (status === 401 || status === 400)
        return { success: false, error: detail };
      if (status === 429)
        return {
          success: false,
          error:
            'Trop de tentatives. Veuillez réessayer dans quelques instants.',
        };

      return { success: false, error: detail };
    }
    return { success: false, error: 'Une erreur réseau est survenue.' };
  }
}

export async function getMeAction(): Promise<MeApiResponse> {
  if (!BASE_URL)
    return { success: false, error: 'Configuration serveur manquante.' };

  const token = await getAccessToken();
  if (!token)
    return { success: false, error: 'Non authentifié.', status: 401 };

  try {
    const { data } = await axios.get<BackendUsersMeResponse>(
      `${BASE_URL}/users/me`,
      { headers: getAuthHeaders(token) }
    );
    return { success: true, message: 'OK', payload: mapUserPayload(data) };
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    return {
      success: false,
      error: handleAxiosError(err, 'Impossible de récupérer le profil.'),
      status,
    };
  }
}

export async function logoutAction(): Promise<LogoutApiResponse> {
  if (!BASE_URL)
    return { success: false, error: 'Configuration serveur manquante.' };

  const token = await getAccessToken();

  if (token) {
    await axios
      .post(
        `${BASE_URL}/users/logout`,
        {},
        { headers: getAuthHeaders(token), validateStatus: () => true }
      )
      .catch(() => null);
  }

  try {
    const { cookies } = await import('next/headers');
    const { ACCESS_TOKEN, REFRESH_TOKEN, NEXT_SESSION } =
      await import('@/features/auth/session');
    const cookieStore = await cookies();
    const opts = { path: '/' };

    cookieStore.delete({ name: ACCESS_TOKEN, ...opts });
    cookieStore.delete({ name: REFRESH_TOKEN, ...opts });
    cookieStore.delete({ name: NEXT_SESSION, ...opts });

    return { success: true, message: 'Déconnexion réussie', payload: {} };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error
          ? err.message
          : 'Erreur lors de la suppression des cookies.',
    };
  }
}

// ─── Password reset actions ───────────────────────────────────────────────────

export interface PasswordResetActionResult {
  success: boolean;
  error?: string;
  /** Présent si l’API renvoie un JSON indiquant un compte admin après reset / activation. */
  activationLoginTarget?: PostActivationLoginTarget;
}

/**
 * POST /auth/ask-password-reset/{email}
 * Sends a password reset link to the given email. Returns 204 on success.
 */
export async function askPasswordResetAction(
  email: string
): Promise<PasswordResetActionResult> {
  if (!BASE_URL)
    return { success: false, error: 'Configuration serveur manquante.' };

  try {
    await axios.post(
      `${BASE_URL}/auth/ask-password-reset/${encodeURIComponent(email)}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        validateStatus: (status) => status === 204 || status === 200,
      }
    );
    return { success: true };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      if (status === 404)
        return { success: false, error: 'Aucun compte associé à cet email.' };
      if (status === 429)
        return {
          success: false,
          error: 'Trop de tentatives. Réessayez dans quelques instants.',
        };
      return {
        success: false,
        error: handleAxiosError(err, "Impossible d'envoyer le lien."),
      };
    }
    return { success: false, error: 'Une erreur réseau est survenue.' };
  }
}

/**
 * POST /auth/reset-password
 * Resets the password using the token received by email. Returns 204 on success.
 */
export async function resetPasswordAction(payload: {
  token: string;
  new_password: string;
  confirm_password: string;
}): Promise<PasswordResetActionResult> {
  if (!BASE_URL)
    return { success: false, error: 'Configuration serveur manquante.' };

  try {
    const res = await axios.post<unknown>(
      `${BASE_URL}/auth/reset-password`,
      {
        token: payload.token,
        new_password: payload.new_password,
        confirm_password: payload.confirm_password,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        validateStatus: () => true,
      }
    );

    if (res.status === 400)
      return {
        success: false,
        error: 'Lien de réinitialisation invalide ou expiré.',
      };
    if (res.status === 404)
      return {
        success: false,
        error: 'Lien de réinitialisation introuvable.',
      };

    if (res.status === 204 || res.status === 205) {
      return { success: true };
    }

    if (res.status >= 200 && res.status < 300) {
      const activationLoginTarget = parseActivationLoginHintFromUnknown(
        res.data
      );
      return {
        success: true,
        ...(activationLoginTarget ? { activationLoginTarget } : {}),
      };
    }

    return {
      success: false,
      error: `Impossible de réinitialiser le mot de passe (réponse ${res.status}).`,
    };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      return {
        success: false,
        error: handleAxiosError(
          err,
          'Impossible de réinitialiser le mot de passe.'
        ),
      };
    }
    return { success: false, error: 'Une erreur réseau est survenue.' };
  }
}

/**
 * getSystemActivitiesAction
 */
export async function getSystemActivities(filters: ActivityFilters) {
  const token = await getAccessToken();

  if (!token) {
    return { ok: false, status: 401, error: 'Non authentifié.' };
  }

  const params = new URLSearchParams();
  params.append('page', String(filters.page ?? 1));
  params.append('page_size', String(filters.page_size ?? 20));

  if (filters.search) params.append('search', filters.search);
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.action_type) params.append('action_type', filters.action_type);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);

  const url = `${BASE_URL}/admin/system/activities?${params.toString()}`;

  try {
    const response = await fetch(url, {
      headers: getAuthHeaders(token),
    });

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: `Erreur ${response.status}: ${response.statusText}`,
      };
    }

    const data = await response.json();

    return {
      ok: true,
      data: {
        ...data,
        results: (data.results || []).map(mapApiActivity),
      },
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Erreur inattendue';
    return { ok: false, status: 0, error: message };
  }
}

export async function getUsers() {
  const token = await getAccessToken();

  if (!token) {
    return { ok: false, status: 401, error: 'Non authentifié.' };
  }

  try {
    const response = await fetch(`${BASE_URL}/admin/users/administrators`, {
      headers: getAuthHeaders(token),
      cache: 'no-store',
    });

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: `Erreur ${response.status}: ${response.statusText}`,
      };
    }

    const data = await response.json();
    console.log('API administrators =', data);

    const rawUsers = Array.isArray(data?.results) ? data.results : [];

    const users = rawUsers
      .map((item: any) => ({
        id: String(item.admin?.id ?? ''),
        name: item.admin?.name ?? 'Utilisateur',
      }))
      .filter((user: any) => user.id && user.name);

    console.log('mapped users =', users);

    return { ok: true, users };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Erreur inattendue';
    return { ok: false, status: 0, error: message };
  }
}

// server/actions/notificationSettings.ts
export async function getNotificationSettings() {
  const token = await getAccessToken();

  if (!token) {
    return { ok: false, status: 401, error: 'Non authentifié.' };
  }

  try {
    const response = await fetch(
      `${BASE_URL}/admin/system/notification-settings`,
      {
        headers: getAuthHeaders(token),
        cache: 'no-store',
      }
    );

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: `Erreur ${response.status}: ${response.statusText}`,
      };
    }

    const data: ApiNotificationSettings = await response.json();
    console.log('API notification-settings =', data);

    // Retourner directement les données API
    return { ok: true, data: data };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Erreur inattendue';
    return { ok: false, status: 0, error: message };
  }
}

// server/actions/configurationSettings.ts
export async function getConfiguration() {
  const token = await getAccessToken();
  if (!token) return { ok: false, status: 401, error: 'Non authentifié.' };

  try {
    const res = await fetch(`${BASE_URL}/admin/system/configuration`, {
      headers: getAuthHeaders(token),
      cache: 'no-store',
    });

    if (!res.ok) {
      return { ok: false, status: res.status, error: await res.text() };
    }

    const data = await res.json();
    return { ok: true, data };
  } catch (err) {
    return { ok: false, status: 500, error: err };
  }
}

export type UpdateSystemConfigurationPayload = {
  scraping?: Record<string, unknown>;
  notifications?: Record<string, unknown>;
  deals?: Record<string, unknown>;
};

export async function updateSystemConfiguration(
  payload: UpdateSystemConfigurationPayload
) {
  const token = await getAccessToken();

  if (!token) {
    return { ok: false, status: 401, error: 'Non authentifié.' };
  }

  try {
    const response = await fetch(`${BASE_URL}/admin/system/configuration`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      cache: 'no-store',
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: data?.message || 'Erreur lors de la mise à jour.',
      };
    }

    return {
      ok: true,
      status: response.status,
      data,
    };
  } catch (error) {
    return {
      ok: false,
      status: 500,
      error:
        error instanceof Error ? error.message : 'Erreur serveur inconnue.',
    };
  }
}

// server/actions/notificationSettingsupdate.ts
export async function updateNotificationSettings(payload: {
  channels?: {
    id: string;
    enabled?: boolean;
  }[];
  events?: {
    id: string;
    enabled?: boolean;
  }[];
}) {
  const token = await getAccessToken();

  if (!token) {
    return { ok: false, status: 401, error: 'Non authentifié.' };
  }

  try {
    const response = await fetch(
      `${BASE_URL}/admin/system/notification-settings`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload),
        cache: 'no-store',
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: data,
      };
    }

    return {
      ok: true,
      status: response.status,
      data,
    };
  } catch (error) {
    return {
      ok: false,
      status: 500,
      error: 'Erreur serveur.',
    };
  }
}
